import { SchemaFieldLabel } from 'src/components/schema-definitions/SchemaFieldLabel';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import type { ImportFieldNode, SchemaFieldDefinitionResponse } from '@erp/rpc';
import { formatFieldTypeLabel } from '@erp/shared-utils';
import { T, useGT } from 'gt-react';

type ReadOnlyFieldTreeProps = {
  fields: SchemaFieldDefinitionResponse[] | ImportFieldNode[];
  depth?: number;
};

function isResponseField(
  field: SchemaFieldDefinitionResponse | ImportFieldNode,
): field is SchemaFieldDefinitionResponse {
  return 'id' in field;
}

function ReadOnlyFieldNode({
  field,
  depth,
}: {
  field: SchemaFieldDefinitionResponse | ImportFieldNode;
  depth: number;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(depth < 2);
  const children = field.children ?? [];
  const hasChildren = children.length > 0;
  const typeLabel = formatFieldTypeLabel(field);

  return (
    <div>
      <div
        className="flex items-start gap-2 py-1 font-mono text-xs"
        style={{ paddingLeft: depth * 16 }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="mt-0.5 text-muted-foreground"
            onClick={() => setOpen((value) => !value)}
            aria-label={
              open
                ? gt('Collapse', {
                    context: 'aria label collapse field tree node',
                  })
                : gt('Expand', {
                    context: 'aria label expand field tree node',
                  })
            }
          >
            {open ? (
              <CaretDownIcon className="h-3 w-3" />
            ) : (
              <CaretRightIcon className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SchemaFieldLabel field={field} />
            <span className="text-muted-foreground">{typeLabel}</span>
            {field.isRequired ? (
              <span className="text-[10px] uppercase text-amber-700">
                <T context="required marker on read-only field tree">
                  required
                </T>
              </span>
            ) : null}
          </div>
          {field.description ? (
            <p className="text-[10px] text-muted-foreground">
              {field.description}
            </p>
          ) : null}
        </div>
      </div>
      {hasChildren && open
        ? children.map((child, index) => (
            <ReadOnlyFieldNode
              key={
                isResponseField(child)
                  ? child.id
                  : `${field.fieldName}-${index}`
              }
              field={child}
              depth={depth + 1}
            />
          ))
        : null}
    </div>
  );
}

export function ReadOnlyFieldTree({
  fields,
  depth = 0,
}: ReadOnlyFieldTreeProps) {
  if (fields.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        <T context="empty read-only field tree">No fields</T>
      </p>
    );
  }

  return (
    <div className="rounded-md border bg-muted/20 p-2">
      {fields.map((field, index) => (
        <ReadOnlyFieldNode
          key={isResponseField(field) ? field.id : `root-${index}`}
          field={field}
          depth={depth}
        />
      ))}
    </div>
  );
}
